import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Card, Tabs, Button, Tag, Space, Modal, Form, Input, InputNumber, Switch, Select, ColorPicker, Alert } from 'antd';
import Table from 'components/Common/Table';
import {
  PlusOutlined,
  SettingOutlined,
  TagsOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import {
  createTicketStatus,
  updateTicketStatus,
  deleteTicketStatus,
  createTicketCategory,
  updateTicketCategory,
  deleteTicketCategory,
  createSLAPolicy,
  updateSLAPolicy,
  deleteSLAPolicy,
  createTicketTemplate,
  updateTicketTemplate,
  deleteTicketTemplate
} from 'store/slices/SupportSlice';

const { TabPane } = Tabs;
const { confirm } = Modal;

const SupportSettings = () => {
  const dispatch = useDispatch();
  const ticketStatuses = useSelector(state => state.support.ticketStatuses);
  const ticketCategories = useSelector(state => state.support.ticketCategories);
  const slaPolicies = useSelector(state => state.support.slaPolicies);
  const ticketTemplates = useSelector(state => state.support.ticketTemplates);
  const configLoading = useSelector(state => state.support.configLoading);
  const templatesLoading = useSelector(state => state.support.templatesLoading);

  // Modal states
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [slaModalVisible, setSlaModalVisible] = useState(false);
  const [templateModalVisible, setTemplateModalVisible] = useState(false);

  // Edit mode states
  const [editingStatus, setEditingStatus] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSla, setEditingSla] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const [statusForm] = Form.useForm();
  const [categoryForm] = Form.useForm();
  const [slaForm] = Form.useForm();
  const [templateForm] = Form.useForm();

  // Status Handlers
  const handleAddStatus = () => {
    setEditingStatus(null);
    statusForm.resetFields();
    setStatusModalVisible(true);
  };

  const handleEditStatus = (record) => {
    setEditingStatus(record);
    statusForm.setFieldsValue({
      name: record.name,
      color: record.color,
      is_default: record.is_default,
      is_closed_status: record.is_closed_status,
      order: record.order
    });
    setStatusModalVisible(true);
  };

  const handleDeleteStatus = (record) => {
    confirm({
      title: 'Delete Status',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete "${record.name}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: () => {
        dispatch(deleteTicketStatus(record.id));
      }
    });
  };

  const handleStatusSubmit = async (values) => {
    try {
      // Convert color object to hex string if needed
      const color = typeof values.color === 'string' ? values.color : values.color?.toHexString();

      const data = {
        ...values,
        color
      };

      if (editingStatus) {
        await dispatch(updateTicketStatus({ id: editingStatus.id, ...data })).unwrap();
      } else {
        await dispatch(createTicketStatus(data)).unwrap();
      }
      setStatusModalVisible(false);
      statusForm.resetFields();
    } catch (error) {
      console.error('Failed to save status:', error);
    }
  };

  // Category Handlers
  const handleAddCategory = () => {
    setEditingCategory(null);
    categoryForm.resetFields();
    setCategoryModalVisible(true);
  };

  const handleEditCategory = (record) => {
    setEditingCategory(record);
    categoryForm.setFieldsValue({
      name: record.name,
      description: record.description,
      icon: record.icon,
      default_priority: record.default_priority,
      default_assigned_team: record.default_assigned_team
    });
    setCategoryModalVisible(true);
  };

  const handleDeleteCategory = (record) => {
    confirm({
      title: 'Delete Category',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete "${record.name}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: () => {
        dispatch(deleteTicketCategory(record.id));
      }
    });
  };

  const handleCategorySubmit = async (values) => {
    try {
      if (editingCategory) {
        await dispatch(updateTicketCategory({ id: editingCategory.id, ...values })).unwrap();
      } else {
        await dispatch(createTicketCategory(values)).unwrap();
      }
      setCategoryModalVisible(false);
      categoryForm.resetFields();
    } catch (error) {
      console.error('Failed to save category:', error);
    }
  };

  // SLA Policy Handlers
  const handleAddSla = () => {
    setEditingSla(null);
    slaForm.resetFields();
    setSlaModalVisible(true);
  };

  const handleEditSla = (record) => {
    setEditingSla(record);
    slaForm.setFieldsValue({
      name: record.name,
      priority: record.priority,
      account_tier: record.account_tier,
      first_response_time: record.first_response_time,
      resolution_time: record.resolution_time,
      business_hours_only: record.business_hours_only,
      is_active: record.is_active
    });
    setSlaModalVisible(true);
  };

  const handleDeleteSla = (record) => {
    confirm({
      title: 'Delete SLA Policy',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete "${record.name}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: () => {
        dispatch(deleteSLAPolicy(record.id));
      }
    });
  };

  const handleSlaSubmit = async (values) => {
    try {
      if (editingSla) {
        await dispatch(updateSLAPolicy({ id: editingSla.id, ...values })).unwrap();
      } else {
        await dispatch(createSLAPolicy(values)).unwrap();
      }
      setSlaModalVisible(false);
      slaForm.resetFields();
    } catch (error) {
      console.error('Failed to save SLA policy:', error);
    }
  };

  // Template Handlers
  const handleAddTemplate = () => {
    setEditingTemplate(null);
    templateForm.resetFields();
    setTemplateModalVisible(true);
  };

  const handleEditTemplate = (record) => {
    setEditingTemplate(record);
    templateForm.setFieldsValue({
      name: record.name,
      title: record.title,
      description: record.description,
      category: record.category,
      priority: record.priority,
      status: record.status
    });
    setTemplateModalVisible(true);
  };

  const handleDeleteTemplate = (record) => {
    confirm({
      title: 'Delete Template',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete "${record.name}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: () => {
        dispatch(deleteTicketTemplate(record.id));
      }
    });
  };

  const handleTemplateSubmit = async (values) => {
    try {
      if (editingTemplate) {
        await dispatch(updateTicketTemplate({ id: editingTemplate.id, ...values })).unwrap();
      } else {
        await dispatch(createTicketTemplate(values)).unwrap();
      }
      setTemplateModalVisible(false);
      templateForm.resetFields();
    } catch (error) {
      console.error('Failed to save template:', error);
    }
  };

  // Statuses Table
  const statusColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Color',
      dataIndex: 'color',
      key: 'color',
      width: 100,
      render: (color) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 20, height: 20, background: color, borderRadius: 4, border: '1px solid #d9d9d9' }} />
          <span>{color}</span>
        </div>
      )
    },
    {
      title: 'Default',
      dataIndex: 'is_default',
      key: 'is_default',
      width: 100,
      render: (isDefault) => isDefault ? <Tag color="blue">Default</Tag> : '-'
    },
    {
      title: 'Closed Status',
      dataIndex: 'is_closed_status',
      key: 'is_closed',
      width: 120,
      render: (isClosed) => isClosed ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>
    },
    {
      title: 'Order',
      dataIndex: 'order',
      key: 'order',
      width: 80,
      align: 'center'
    },
    {
      title: 'Tickets',
      dataIndex: 'ticket_count',
      key: 'ticket_count',
      width: 80,
      align: 'center'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => handleEditStatus(record)}>Edit</Button>
          <Button type="link" size="small" danger onClick={() => handleDeleteStatus(record)}>Delete</Button>
        </Space>
      )
    }
  ];

  // Categories Table
  const categoryColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <span>
          {record.icon && <span style={{ marginRight: 8 }}>{record.icon}</span>}
          <strong>{text}</strong>
        </span>
      )
    },
    {
      title: 'Default Priority',
      dataIndex: 'default_priority',
      key: 'default_priority',
      width: 140,
      render: (priority, record) => (
        <Tag color={
          priority === 'CRITICAL' ? 'red' :
          priority === 'HIGH' ? 'orange' :
          priority === 'MEDIUM' ? 'blue' :
          'default'
        }>
          {record.default_priority_display || priority}
        </Tag>
      )
    },
    {
      title: 'Default Team',
      dataIndex: 'default_assigned_team',
      key: 'default_team',
      render: (text) => text || '-'
    },
    {
      title: 'Tickets',
      dataIndex: 'ticket_count',
      key: 'ticket_count',
      width: 80,
      align: 'center'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => handleEditCategory(record)}>Edit</Button>
          <Button type="link" size="small" danger onClick={() => handleDeleteCategory(record)}>Delete</Button>
        </Space>
      )
    }
  ];

  // SLA Policies Table
  const slaColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Account Tier',
      dataIndex: 'account_tier_display',
      key: 'tier',
      width: 120,
      render: (text, record) => (
        record.specific_account_name ? (
          <Tag color="purple">{record.specific_account_name}</Tag>
        ) : (
          <Tag color="blue">{text || 'All'}</Tag>
        )
      )
    },
    {
      title: 'Priority',
      dataIndex: 'priority_display',
      key: 'priority',
      width: 100,
      render: (text, record) => (
        <Tag color={
          record.priority === 'CRITICAL' ? 'red' :
          record.priority === 'HIGH' ? 'orange' :
          record.priority === 'MEDIUM' ? 'blue' :
          'default'
        }>
          {text}
        </Tag>
      )
    },
    {
      title: 'First Response',
      dataIndex: 'first_response_time',
      key: 'first_response',
      width: 130,
      render: (minutes) => `${minutes} min`
    },
    {
      title: 'Resolution',
      dataIndex: 'resolution_time',
      key: 'resolution',
      width: 120,
      render: (minutes) => {
        const hours = Math.floor(minutes / 60);
        return hours > 0 ? `${hours} hrs` : `${minutes} min`;
      }
    },
    {
      title: 'Business Hours',
      dataIndex: 'business_hours_only',
      key: 'business_hours',
      width: 130,
      render: (businessHours) => businessHours ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (isActive) => isActive ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => handleEditSla(record)}>Edit</Button>
          <Button type="link" size="small" danger onClick={() => handleDeleteSla(record)}>Delete</Button>
        </Space>
      )
    }
  ];

  // Template Columns
  const templateColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true
    },
    {
      title: 'Category',
      dataIndex: 'category_name',
      key: 'category',
      width: 150
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority, record) => (
        <Tag color={
          priority === 'CRITICAL' ? 'red' :
          priority === 'HIGH' ? 'orange' :
          priority === 'MEDIUM' ? 'blue' :
          'default'
        }>
          {record.priority_display || priority}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => handleEditTemplate(record)}>Edit</Button>
          <Button type="link" size="small" danger onClick={() => handleDeleteTemplate(record)}>Delete</Button>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px', minHeight: '100vh' }}>
      <Card title={
        <span><SettingOutlined /> Support Settings</span>
      }>
        <Tabs defaultActiveKey="1" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
          <TabPane
            tab={<span><TagsOutlined /> Ticket Statuses</span>}
            key="1"
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              style={{ marginBottom: 16 }}
              onClick={handleAddStatus}
            >
              Add Status
            </Button>
            <Table
              columns={statusColumns}
              dataSource={ticketStatuses}
              rowKey="id"
              pagination={false}
              loading={configLoading}
            />
          </TabPane>

          <TabPane
            tab={<span><TagsOutlined /> Ticket Categories</span>}
            key="2"
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              style={{ marginBottom: 16 }}
              onClick={handleAddCategory}
            >
              Add Category
            </Button>
            <Table
              columns={categoryColumns}
              dataSource={ticketCategories}
              rowKey="id"
              pagination={false}
              loading={configLoading}
            />
          </TabPane>

          <TabPane
            tab={<span><ClockCircleOutlined /> SLA Policies</span>}
            key="3"
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              style={{ marginBottom: 16 }}
              onClick={handleAddSla}
            >
              Add SLA Policy
            </Button>
            <Table
              columns={slaColumns}
              dataSource={slaPolicies}
              rowKey="id"
              pagination={false}
              loading={configLoading}
              scroll={{ x: 1200 }}
            />
          </TabPane>

          <TabPane
            tab={<span><FileTextOutlined /> Ticket Templates</span>}
            key="4"
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              style={{ marginBottom: 16 }}
              onClick={handleAddTemplate}
            >
              Add Template
            </Button>
            <Table
              columns={templateColumns}
              dataSource={ticketTemplates}
              rowKey="id"
              pagination={false}
              loading={templatesLoading}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* Status Modal */}
      <Modal
        title={editingStatus ? 'Edit Status' : 'Add Status'}
        open={statusModalVisible}
        onCancel={() => {
          setStatusModalVisible(false);
          statusForm.resetFields();
        }}
        onOk={() => statusForm.submit()}
        width={600}
        bodyStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
      >
        <Form
          form={statusForm}
          layout="vertical"
          onFinish={handleStatusSubmit}
          initialValues={{
            is_default: false,
            is_closed_status: false,
            order: 0,
            color: '#1890ff'
          }}
        >
          <Form.Item
            name="name"
            label="Status Name"
            rules={[{ required: true, message: 'Please enter status name' }]}
          >
            <Input placeholder="e.g., Open, In Progress, Resolved" />
          </Form.Item>

          <Form.Item
            name="color"
            label="Color"
            rules={[{ required: true, message: 'Please select a color' }]}
          >
            <ColorPicker showText />
          </Form.Item>

          <Form.Item
            name="order"
            label="Display Order"
            rules={[{ required: true, message: 'Please enter display order' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="is_default"
            label="Default Status"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="is_closed_status"
            label="Closed Status"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Category Modal */}
      <Modal
        title={editingCategory ? 'Edit Category' : 'Add Category'}
        open={categoryModalVisible}
        onCancel={() => {
          setCategoryModalVisible(false);
          categoryForm.resetFields();
        }}
        onOk={() => categoryForm.submit()}
        width={600}
        bodyStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
      >
        <Form
          form={categoryForm}
          layout="vertical"
          onFinish={handleCategorySubmit}
          initialValues={{
            default_priority: 'MEDIUM'
          }}
        >
          <Form.Item
            name="name"
            label="Category Name"
            rules={[{ required: true, message: 'Please enter category name' }]}
          >
            <Input placeholder="e.g., Technical Issue, Billing, Feature Request" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea rows={3} placeholder="Description of this category" />
          </Form.Item>

          <Form.Item
            name="icon"
            label="Icon (Emoji)"
          >
            <Input placeholder="e.g., 🔧, 💰, ✨" maxLength={2} />
          </Form.Item>

          <Form.Item
            name="default_priority"
            label="Default Priority"
            rules={[{ required: true, message: 'Please select default priority' }]}
          >
            <Select>
              <Select.Option value="LOW">Low</Select.Option>
              <Select.Option value="MEDIUM">Medium</Select.Option>
              <Select.Option value="HIGH">High</Select.Option>
              <Select.Option value="CRITICAL">Critical</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="default_assigned_team"
            label="Default Assigned Team"
          >
            <Input placeholder="e.g., Support Team, Engineering" />
          </Form.Item>
        </Form>
      </Modal>

      {/* SLA Policy Modal */}
      <Modal
        title={editingSla ? 'Edit SLA Policy' : 'Add SLA Policy'}
        open={slaModalVisible}
        onCancel={() => {
          setSlaModalVisible(false);
          slaForm.resetFields();
        }}
        onOk={() => slaForm.submit()}
        width={700}
        bodyStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
      >
        <Alert
          message="Service Level Agreement (SLA) Policy"
          description="Define response and resolution time targets for different ticket priorities and account tiers."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form
          form={slaForm}
          layout="vertical"
          onFinish={handleSlaSubmit}
          initialValues={{
            priority: 'MEDIUM',
            first_response_time: 60,
            resolution_time: 480,
            business_hours_only: true,
            is_active: true
          }}
        >
          <Form.Item
            name="name"
            label="Policy Name"
            rules={[{ required: true, message: 'Please enter policy name' }]}
          >
            <Input placeholder="e.g., Standard Support, Premium Support" />
          </Form.Item>

          <Form.Item
            name="priority"
            label="Ticket Priority"
            rules={[{ required: true, message: 'Please select priority' }]}
          >
            <Select>
              <Select.Option value="LOW">Low</Select.Option>
              <Select.Option value="MEDIUM">Medium</Select.Option>
              <Select.Option value="HIGH">High</Select.Option>
              <Select.Option value="CRITICAL">Critical</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="account_tier"
            label="Account Tier"
          >
            <Select allowClear placeholder="Select account tier (optional)">
              <Select.Option value="FREE">Free</Select.Option>
              <Select.Option value="BASIC">Basic</Select.Option>
              <Select.Option value="PREMIUM">Premium</Select.Option>
              <Select.Option value="ENTERPRISE">Enterprise</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="first_response_time"
            label="First Response Time (minutes)"
            rules={[{ required: true, message: 'Please enter first response time' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="resolution_time"
            label="Resolution Time (minutes)"
            rules={[{ required: true, message: 'Please enter resolution time' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="business_hours_only"
            label="Business Hours Only"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Template Modal */}
      <Modal
        title={editingTemplate ? 'Edit Template' : 'Add Template'}
        open={templateModalVisible}
        onCancel={() => {
          setTemplateModalVisible(false);
          templateForm.resetFields();
        }}
        onOk={() => templateForm.submit()}
        width={700}
        bodyStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
      >
        <Form
          form={templateForm}
          layout="vertical"
          onFinish={handleTemplateSubmit}
          initialValues={{
            priority: 'MEDIUM'
          }}
        >
          <Form.Item
            name="name"
            label="Template Name"
            rules={[{ required: true, message: 'Please enter template name' }]}
          >
            <Input placeholder="e.g., Bug Report, Feature Request" />
          </Form.Item>

          <Form.Item
            name="title"
            label="Default Title"
            rules={[{ required: true, message: 'Please enter default title' }]}
          >
            <Input placeholder="Ticket title template" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Default Description"
            rules={[{ required: true, message: 'Please enter default description' }]}
          >
            <Input.TextArea
              rows={5}
              placeholder="Template description with placeholders..."
            />
          </Form.Item>

          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Please select a category' }]}
          >
            <Select placeholder="Select category">
              {ticketCategories.map(cat => (
                <Select.Option key={cat.id} value={cat.id}>
                  {cat.icon && <span style={{ marginRight: 8 }}>{cat.icon}</span>}
                  {cat.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="priority"
            label="Default Priority"
            rules={[{ required: true, message: 'Please select priority' }]}
          >
            <Select>
              <Select.Option value="LOW">Low</Select.Option>
              <Select.Option value="MEDIUM">Medium</Select.Option>
              <Select.Option value="HIGH">High</Select.Option>
              <Select.Option value="CRITICAL">Critical</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label="Default Status"
          >
            <Select placeholder="Select default status (optional)">
              {ticketStatuses.map(status => (
                <Select.Option key={status.id} value={status.id}>
                  {status.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SupportSettings;
